import { lazy, ReactNode, Suspense, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { motion } from 'motion/react';

import { DecryptText } from '@components/patterns/DecryptText';
import { Button, Dialog, DialogState, Link } from '@components/primitives';

import { labelForPlan } from '@components/features/Settings/TeamBilling/utils';
import getStripeCheckoutUrl from '@utils/fetch/getStripeCheckoutUrl';

import css from './styles.module.css';

const Pixels = lazy(() => import('./Pixels'));

type UpgradeModalProps = {
  teamUuid: string;
  state: DialogState;
  title?: string;
  subText?: string;
  trigger?: ReactNode;
};

const DEFAULT_TITLE = `Upgrade to ${labelForPlan('pro')}`;
const DEFAULT_SUB_TEXT =
  'Handle real user data with unlimited production apps and full access to encryption and payments features.';

export function UpgradeModal({
  teamUuid,
  state,
  title = DEFAULT_TITLE,
  trigger,
  subText,
}: UpgradeModalProps) {
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const stripeCheckoutUrl = await getStripeCheckoutUrl(teamUuid);
      window.open(stripeCheckoutUrl, '_self');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog state={state} trigger={trigger} className={css.modal}>
      <div className={css.asset}>
        <Suspense fallback={null}>
          <motion.div
            className={css.pixels}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <Pixels />
          </motion.div>
        </Suspense>
        <div className={css.logo}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="73"
            height="80"
            fill="none"
            viewBox="0 0 73 80"
          >
            <path
              fill="#fff"
              fillRule="evenodd"
              d="M2.84 10.5a5.519 5.519 0 00-2.822 4.372c-.01-.14-.016-.28-.018-.421v17.78c.003-.117.009-.232.018-.346.207 2.822 2.078 5.262 4.765 6.151l3.271 1.083-5.213 2.877a5.519 5.519 0 00-2.823 4.372c-.01-.14-.016-.28-.018-.421v17.78c.003-.116.009-.232.018-.346.207 2.822 2.078 5.262 4.765 6.151l26.298 8.707a34.745 34.745 0 0027.728-2.574l11.17-6.164a5.519 5.519 0 002.823-4.373c.01.14.017.28.018.421V47.77a5.502 5.502 0 01-.018.346c-.206-2.822-2.078-5.262-4.765-6.151l-3.27-1.083 5.213-2.876a5.519 5.519 0 002.822-4.373c.01.14.017.28.018.421v-17.78a5.5 5.5 0 01-.018.346c-.206-2.822-2.078-5.262-4.765-6.151L41.74 1.76a34.746 34.746 0 00-27.728 2.574L2.841 10.5zm8.05 29.558l-7.008 3.866a3.322 3.322 0 00-1.708 2.817v.503c.04 2.677 1.846 5.034 4.498 5.867l1.477.465 11.212-6.187a20.26 20.26 0 016.747-2.293L10.89 40.058zm32.423 8.421a34.788 34.788 0 0015.495-4.31l3.34-1.843 5.212 1.726a4.818 4.818 0 013.287 4.501v.318c-.041 1.762-1.06 3.368-2.671 4.212l-4.166 2.182-20.497-6.786zM31.399 29.387l-20.36-6.399 9.363-5.166a18.068 18.068 0 0114.419-1.338l26.284 8.702-3.634 1.903c-7.988 4.184-17.423 5.016-26.072 2.298zm4.098-14.992L63.81 23.77l4.166-2.182c1.611-.844 2.63-2.45 2.67-4.212v-.318a4.818 4.818 0 00-3.285-4.501L41.063 3.85a32.593 32.593 0 00-26.01 2.414l-11.17 6.164a3.322 3.322 0 00-1.71 2.817v.503c.04 2.677 1.847 5.034 4.499 5.867l1.477.465 11.212-6.187a20.22 20.22 0 0116.136-1.498zM31.4 60.883l-20.36-6.399 9.363-5.166a18.067 18.067 0 0114.419-1.338l26.284 8.702-3.634 1.903c-7.988 4.185-17.423 5.016-26.072 2.298z"
              clipRule="evenodd"
            ></path>
          </svg>
        </div>
      </div>
      <div className={css.content}>
        <h1>{title}</h1>
        <p className={css.description}>{subText ?? DEFAULT_SUB_TEXT}</p>
        <div className={css.price}>
          <span className={css.amount}>$995</span>
          <span className={css.per}>/ month</span>
        </div>
        <ul className={css.features}>
          <Feature title="Card Collection" />
          <Feature
            title="3D Secure"
            description="$0.03 per successful authentication"
          />
          <Feature
            title="Network Tokens"
            description="$0.03 per network token event"
          />
          <Feature
            title="Unlimited Decrypts"
            description="$0.005 per decrypt"
          />
          <Feature title="Unlimited Production Apps" />
          <Feature title="Unlimited Team Members" />
        </ul>

        <Link
          className={css.pricingLink}
          href="https://evervault.com/pricing"
          target="_blank"
        >
          See full pricing<span aria-hidden> {'->'}</span>
        </Link>

        <div className={css.actions}>
          <Button onClick={handleUpgrade} loading={loading} variant="primary">
            Upgrade
          </Button>
          <Button ghost onClick={state.close} size="sm">
            Cancel
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

type FeatureProps = {
  title: string;
  description?: string;
};

function Feature({ title, description }: FeatureProps) {
  return (
    <li>
      <CheckCircle2 />
      <h5>
        <DecryptText>{title}</DecryptText>
      </h5>
      {description && (
        <p>
          <DecryptText>{description}</DecryptText>
        </p>
      )}
    </li>
  );
}
